import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { environment } from 'src/environments/environment';
import { IdName } from '../../shared/models/id-name';
import { IDepartment, IDepartmentUser, IDepartmentWithGroups } from '../models/department.model';
import { DepartmentService } from './department.service';

@Injectable({
  providedIn: 'root',
})
export class DepartmentApiService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    params: new HttpParams(),
  };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/departments`;
  }

  private urlForSelectedTenant(tenantId: string): string {
    return `${environment.apiUrl}/api/${tenantId}/departments`;
  }

  constructor(private http: HttpClient) {}

  getDepartments(currentFilter?: MainTableFilterModel): Observable<ApiResponse<IDepartment[]>> {
    const params = createFilterParams(currentFilter);

    return this.http.get<ApiResponse<IDepartment[]>>(this.url, { ...this.httpOptions, params });
  }

  getAllDepartments(excludeArchived: boolean, onlyVessels?: boolean): Observable<IdName[]> {
    const url = `${this.url}/List`;
    let queryParams = new HttpParams();
    queryParams = queryParams.set('ExcludeArchived', excludeArchived?.toString());
    if (onlyVessels) {
      queryParams = queryParams.append('OnlyVessel', onlyVessels.toString());
    }
    return this.http.get<IdName[]>(url, { ...this.httpOptions, params: queryParams });
  }

  getAccessedByEmployment(): Observable<IdName[]> {
    const url = `${this.url}/accessedByEmployment`;
    let queryParams = new HttpParams();
    queryParams = queryParams.append('ExcludeArchived', 'true');
    return this.http.get<IdName[]>(url, { ...this.httpOptions, params: queryParams });
  }

  getAllDepartmentsForSelectedTenant(
    tenantId: string,
    excludeArchived: boolean = true
  ): Observable<IdName[]> {
    const url = `${this.urlForSelectedTenant(tenantId || getTenantId())}/List`;
    let queryParams = new HttpParams();
    queryParams = queryParams.set('ExcludeArchived', excludeArchived.toString());
    return this.http.get<IdName[]>(url, { ...this.httpOptions, params: queryParams });
  }

  getAllDepartmentsForMenuId(
    menuId: string,
    excludeArchived: boolean = true
  ): Observable<IdName[]> {
    const url = `${environment.apiUrl}/api/${getTenantId()}/menu/${menuId}/departments`;
    const queryParams = new HttpParams().set('ExcludeArchived', excludeArchived.toString());
    return this.http.get<IdName[]>(url, { ...this.httpOptions, params: queryParams });
  }

  getDepartmentsForUser(excludeArchived: boolean = true): Observable<IDepartmentWithGroups[]> {
    const url = `${this.url}/forUser`;
    const queryParams = new HttpParams().set('ExcludeArchived', excludeArchived.toString());
    return this.http.get<IDepartmentWithGroups[]>(url, {
      ...this.httpOptions,
      params: queryParams,
    });
  }

  getDepartmentUsers(
    departmentId: string,
    config: {
      includeDeleted?: boolean;
      onlyEmployed?: boolean;
    }
  ): Observable<IDepartmentUser[]> {
    const { includeDeleted, onlyEmployed } = config;
    const params = new HttpParams()
      .set('IncludeDeleted', !!includeDeleted)
      .set('OnlyEmployed', !!onlyEmployed);
    const url = `${this.url}/${departmentId}/users`;
    return this.http.get<IDepartmentUser[]>(url, { ...this.httpOptions, params });
  }

  getDepartmentById(departmentId: string) {
    const url = `${this.url}/${departmentId}`;
    return this.http.get<IDepartment>(url);
  }

  createDepartment(department: IDepartment): Observable<Response> {
    const formData = DepartmentService.convertToFormData(department, getTenantId());
    return this.http.post<Response>(this.url, formData);
  }

  updateDepartment(department: IDepartment): Observable<IDepartment> {
    const formData = DepartmentService.convertToFormData(department, getTenantId());
    return this.http.put<IDepartment>(this.url, formData);
  }

  deleteDepartment(departmentId: string): Observable<IDepartment> {
    const url = `${this.url}/${departmentId}`;
    return this.http.delete<IDepartment>(url, this.httpOptions);
  }
}
